// Alerts, alert rules (per-channel delivery: inbox / push / email × roles × severities — IMPLEMENTATION_PLAN §3.5
// P2-1) and the notification delivery log. RLS decides visibility: users see alerts of their sites; rules and the
// outbox are for managers.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

export const ALERT_KINDS = [
  'left_site',
  'missing',
  'awaiting_receipt',
  'transfer_received',
  'transfer_disputed',
  'transfer_stuck',
  'gateway_down',
  'tag_silent',
  'low_battery',
  'count_due',
  'count_overdue',
  // a certificate or the purchase warranty of a live asset (CR_20260921_Asset_Lifecycle L9 / L9a)
  'cert_expiring',
  // requests from a warehouse (CR_20260915_Request_Qty §7): who hears them comes from the request, not the roles
  'request_submitted',
  'request_approved',
  'request_rejected',
  'request_closed_short',
  'request_expired',
] as const;
/** Kinds sent to named people (the requester, the approvers, the source's staff): the role filter does not apply. */
export const REQUEST_KINDS: readonly AlertKind[] = [
  'request_submitted',
  'request_approved',
  'request_rejected',
  'request_closed_short',
  'request_expired',
];
export type AlertKind = (typeof ALERT_KINDS)[number];
/** Alert channels, in the order the rule editor shows them. WhatsApp only carries invitations (delivery log). */
export const CHANNELS = ['inbox', 'push', 'email'] as const;
export type Channel = (typeof CHANNELS)[number];
export type OutboxChannel = 'whatsapp' | 'email' | 'push';
export type Role = 'manager' | 'user';
export const ROLES: Role[] = ['manager', 'user'];
export type Severity = 'info' | 'warning' | 'critical';
export const SEVERITIES: Severity[] = ['info', 'warning', 'critical'];

export interface ChannelDelivery {
  enabled: boolean;
  roles: Role[];
  /** empty = every severity */
  severities: Severity[];
}
export type Delivery = Record<Channel, ChannelDelivery>;

/** Same defaults as app.default_delivery() when a company has no rule row (or a channel is not set in it). */
export const DEFAULT_ROLES: Record<AlertKind, Role[]> = {
  left_site: ['manager', 'user'],
  missing: ['manager', 'user'],
  awaiting_receipt: ['manager', 'user'],
  transfer_received: ['manager', 'user'],
  count_due: ['manager', 'user'],
  transfer_stuck: ['manager', 'user'],
  transfer_disputed: ['manager'],
  gateway_down: ['manager'],
  tag_silent: ['manager'],
  low_battery: ['manager'],
  count_overdue: ['manager'],
  cert_expiring: ['manager'],
  request_submitted: ['manager'],
  request_approved: ['manager'],
  request_rejected: ['manager'],
  request_closed_short: ['manager'],
  request_expired: ['manager'],
};
export function defaultDelivery(kind: AlertKind): Delivery {
  const one = (): ChannelDelivery => ({
    enabled: true,
    roles: [...DEFAULT_ROLES[kind]],
    severities: [],
  });
  return { inbox: one(), push: one(), email: one() };
}

/** What app.notify uses: the kind's default overlaid with whatever the rule sets, channel by channel. */
export function effectiveDelivery(
  kind: AlertKind,
  stored: Partial<Record<Channel, Partial<ChannelDelivery>>> | null,
): Delivery {
  const d = defaultDelivery(kind);
  for (const ch of CHANNELS) {
    const s = stored?.[ch];
    if (s) d[ch] = { enabled: !!s.enabled, roles: s.roles ?? [], severities: s.severities ?? [] };
  }
  return d;
}

export interface AlertRow {
  id: string;
  rule: string;
  severity: 'info' | 'warning' | 'critical';
  message: string;
  payload: Record<string, unknown>;
  created_at: string;
  acknowledged_at: string | null;
  resolved_at: string | null;
  asset_id: string | null;
  site_id: string | null;
  assets: { asset_code: string } | null;
  sites: { name: string } | null;
  gateways: { display_name: string | null; identifier: string } | null;
  transfers: { ref: string } | null;
}

export interface RuleRow {
  id?: string;
  rule: AlertKind;
  is_active: boolean;
  delivery: Delivery;
  params: Record<string, unknown>;
}

export interface OutboxRow {
  id: number;
  created_at: string;
  template: string;
  channel: OutboxChannel;
  status: 'pending' | 'sending' | 'sent' | 'failed' | 'cancelled';
  attempts: number;
  last_error: string | null;
  sent_at: string | null;
  next_attempt_at: string;
}

export const alertKeys = {
  list: (all: boolean) => ['alerts', all] as const,
  openCount: ['alerts', 'open-count'] as const,
  rules: ['alert-rules'] as const,
  outbox: ['outbox'] as const,
};

async function data<T>(
  q: PromiseLike<{ data: unknown; error: { message: string } | null }>,
): Promise<T> {
  const { data: d, error } = await q;
  if (error) throw new Error(error.message);
  return d as T;
}

export function useAlerts(includeResolved: boolean) {
  return useQuery({
    queryKey: alertKeys.list(includeResolved),
    queryFn: () => {
      let q = supabase()
        .from('alerts')
        .select(
          'id, rule, severity, message, payload, created_at, acknowledged_at, resolved_at, asset_id, site_id, assets(asset_code), sites(name), gateways(display_name, identifier), transfers(ref)',
        )
        .order('created_at', { ascending: false })
        .limit(200);
      if (!includeResolved) q = q.is('resolved_at', null);
      return data<AlertRow[]>(q);
    },
  });
}

export function useOpenAlertCount(enabled: boolean) {
  return useQuery({
    queryKey: alertKeys.openCount,
    enabled,
    queryFn: async () => {
      const { count, error } = await supabase()
        .from('alerts')
        .select('id', { count: 'exact', head: true })
        .is('resolved_at', null)
        .is('acknowledged_at', null);
      if (error) throw new Error(error.message);
      return count ?? 0;
    },
    refetchInterval: 60_000,
  });
}

export function useAcknowledge() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, resolve }: { id: string; resolve: boolean }) =>
      data(supabase().rpc('acknowledge_alert', { p_alert_id: id, p_resolve: resolve })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['alerts'] }),
  });
}

/** The company's rules. Filtered by company too: a super admin's RLS carve-out returns every company's rows. */
export function useRules(companyId: string | undefined) {
  return useQuery({
    queryKey: alertKeys.rules,
    enabled: !!companyId,
    queryFn: async () => {
      const rows = await data<
        {
          id: string;
          rule: AlertKind;
          is_active: boolean;
          delivery: Partial<Record<Channel, Partial<ChannelDelivery>>>;
          params: Record<string, unknown>;
        }[]
      >(
        supabase()
          .from('alert_rules')
          .select('id, rule, is_active, delivery, params')
          .eq('company_id', companyId!),
      );
      return ALERT_KINDS.map((kind): RuleRow => {
        const row = rows.find((r) => r.rule === kind);
        return {
          id: row?.id,
          rule: kind,
          is_active: row?.is_active ?? true,
          delivery: effectiveDelivery(kind, row?.delivery ?? null),
          params: row?.params ?? {},
        };
      });
    },
  });
}

export function useSaveRule(companyId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rule: RuleRow) => {
      // rules belong to one company; a super admin outside any company has none to save to (fail closed)
      if (!companyId) return Promise.reject(new Error('No company to save the alert rule for'));
      return data(
        supabase().from('alert_rules').upsert(
          {
            company_id: companyId,
            rule: rule.rule,
            is_active: rule.is_active,
            delivery: rule.delivery,
            params: rule.params,
          },
          { onConflict: 'company_id,rule' },
        ),
      );
    },
    // optimistic: the switch/checkbox moves at once; a refusal rolls back
    onMutate: async (rule: RuleRow) => {
      await queryClient.cancelQueries({ queryKey: alertKeys.rules });
      const before = queryClient.getQueryData<RuleRow[]>(alertKeys.rules);
      queryClient.setQueryData<RuleRow[]>(alertKeys.rules, (rows) =>
        rows?.map((r) => (r.rule === rule.rule ? rule : r)),
      );
      return { before };
    },
    onError: (_err, _rule, ctx) => queryClient.setQueryData(alertKeys.rules, ctx?.before),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: alertKeys.rules }),
  });
}

export function useOutbox() {
  return useQuery({
    queryKey: alertKeys.outbox,
    queryFn: () =>
      data<OutboxRow[]>(
        supabase()
          .from('notifications_outbox')
          .select(
            'id, created_at, template, channel, status, attempts, last_error, sent_at, next_attempt_at',
          )
          .order('created_at', { ascending: false })
          .limit(50),
      ),
  });
}
